import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Heading, Modal } from '@zillow/constellation-10';

import PaymentDetails from '../../Payment/PaymentDetails';

import PropertiesActions from '../../../actions/PropertiesActions';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';

import type { FreemiumChargeSuccessProps } from '../../../model/FreemiumChargeSuccess';
import type { ErrorRMWProps } from '../../../model/ErrorRMW';

type Props = {
  handleFreemiumChargeSuccess: (response: FreemiumChargeSuccessProps) => void;
  onFreemiumChargeError: (error: ErrorRMWProps) => void;
  listingAlias: string;
  isRenderedInPageView: boolean;
  onClose: () => void;
  onPaymentMethodChange?: () => void;
};

/**
 * We want to disable closing the modal when a payment is processing.
 * We add the logic here as we need to prevent outside click, esc, and close btn click actions
 * and the logic needed is within the child component.
 *
 * Once the payment is complete we close the modal automatically from PremiumPaymentGateway
 * via component removal
 *
 */

const PaymentModal = ({
  handleFreemiumChargeSuccess,
  onFreemiumChargeError,
  listingAlias,
  isRenderedInPageView,
  onClose,
  onPaymentMethodChange,
}: Props) => {
  const dispatch = useDispatch();

  const [isChargeFetching, setIsChargeFetching] = useState(true);
  const [headingText, setHeadingText] = useState('Credit card information');

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // If user closes modal without upgrading, reset the selected product type to free
      dispatch(
        PropertiesActions.updateCurrentProperty({
          selectedProductType: PRODUCT_TYPE_NAME_TO_ID.FREE,
        }),
      );
      onClose();
    }
  };

  return (
    <Modal
      useDismissProps={{ escapeKey: isChargeFetching === false, outsidePress: isChargeFetching === false }}
      closeButton={isChargeFetching === false ? <Modal.CloseButton /> : null}
      header={<Heading level={2}>{headingText}</Heading>}
      body={
        <PaymentDetails
          listingAlias={listingAlias}
          isRenderedInPageView={isRenderedInPageView}
          handleFreemiumChargeSuccess={handleFreemiumChargeSuccess}
          onFreemiumChargeError={onFreemiumChargeError}
          onPaymentMethodChange={onPaymentMethodChange}
          setIsChargeFetching={setIsChargeFetching}
          setHeadingText={setHeadingText}
        />
      }
      onOpenChange={handleOpenChange}
      open={true}
      size={{ base: 'fullScreen', md: 'md' }}
    />
  );
};

export default PaymentModal;
